import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Separator } from "./ui/separator";
import {
  MapPin,
  DollarSign,
  TrendingUp,
  Calendar,
  User,
  FileText,
  Camera,
  Video,
  Building2,
  Target,
  Wrench,
  X,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Shield,
  Loader2,
  MessageSquare,
  Home,
  UserCircle,
  AlertTriangle,
  ImageOff,
  FileWarning,
  Sparkles,
  Send,
  Ban,
  Edit3,
  Eye,
  Download,
  Play,
  Upload,
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { fetchViewUrl } from "../lib/s3Upload";
import { formatCurrencyInput, parseCurrency } from "../lib/currency";
import usePresignedUrl from "../hooks/usePresignedUrl";

// Fetches a fresh presigned URL for a deal NDA S3 key on demand
const NdaViewButton = ({ s3Key }) => {
  const [loading, setLoading] = useState(false);
  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const handleView = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      const url = res.data?.deal_nda;
      if (url) window.open(url, "_blank");
      else toast.error("Deal NDA document not available");
    } catch {
      toast.error("Failed to load document");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleView}
      disabled={loading}
      className="gap-1.5 mt-1"
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Eye className="w-3.5 h-3.5" />
      )}
      View Deal NDA
    </Button>
  );
};

const NdaReplaceSection = ({ dealId, onReplaced }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("PDF files only");
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error("Max 25 MB");
      return;
    }
    setFile(f);
  };
  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const headers = getAuthHeaders();
      const presign = await axios.post(
        `${API}/uploads/presign`,
        {
          filename: file.name,
          content_type: "application/pdf",
          category: "nda",
        },
        { withCredentials: true, headers },
      );
      const { upload_url, fields, storage_path } = presign.data;
      const fd = new FormData();
      Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
      fd.append("file", file);
      await axios.post(upload_url, fd);
      await axios.put(
        `${API}/admin/deals/${dealId}/nda`,
        { s3_key: storage_path },
        { withCredentials: true, headers },
      );
      toast.success("Deal NDA replaced successfully");
      setFile(null);
      if (onReplaced) onReplaced();
    } catch {
      toast.error("Failed to upload NDA");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="mt-2 space-y-2 pl-1">
      <input
        type="file"
        accept=".pdf"
        onChange={handleFile}
        className="text-xs text-muted-foreground"
      />
      {file && (
        <Button
          size="sm"
          onClick={handleUpload}
          disabled={uploading}
          className="gap-1.5"
        >
          {uploading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          {uploading ? "Uploading..." : `Attach "${file.name}"`}
        </Button>
      )}
    </div>
  );
};

const ReplaceNdaToggle = ({ dealId, onReplaced }) => {
  const [checked, setChecked] = useState(false);
  return (
    <div className="mt-2 space-y-2">
      <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground hover:text-foreground">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          className="rounded"
        />
        Attach a different NDA to this deal
      </label>
      {checked && (
        <NdaReplaceSection
          dealId={dealId}
          onReplaced={() => {
            setChecked(false);
            onReplaced();
          }}
        />
      )}
    </div>
  );
};

const DEAL_TYPE_LABELS = {
  flip: "Flip",
  rental: "Rental",
  development: "Development",
};

const DEAL_TYPE_COLORS = {
  flip: "bg-orange-500/90 text-white",
  rental: "bg-blue-500/90 text-white",
  development: "bg-purple-500/90 text-white",
};

const PROPERTY_TYPE_LABELS = {
  single_family: "Single Family",
  multi_family: "Multi-Family",
  apartment: "Apartment",
  commercial: "Commercial",
  land: "Land",
  mixed_use: "Mixed Use",
};

const STATUS_LABELS = {
  pending: "Pending Review",
  active: "Live",
  approved: "Approved",
  reserved: "Under Contract",
  in_closing: "In Closing",
  closed: "Closed",
  rejected: "Rejected",
  changes_requested: "Changes Requested",
};

const STATUS_COLORS = {
  pending: "bg-orange-500 text-white",
  active: "bg-green-500 text-white",
  approved: "bg-green-500 text-white",
  reserved: "bg-blue-500 text-white",
  in_closing: "bg-purple-500 text-white",
  closed: "bg-gray-500 text-white",
  rejected: "bg-red-500 text-white",
  changes_requested: "bg-yellow-500 text-white",
};

// Photo categories for verification
const PHOTO_CATEGORIES = [
  { key: "exterior", label: "Exterior Photos", required: true },
  { key: "interior", label: "Interior Photos", required: true },
  { key: "damage", label: "Damage/Repair Areas", required: false },
];

// Document types for verification — fetched dynamically via useDealDocuments hook

const DealReviewModal = ({ deal, open, onOpenChange, onDealUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");
  const [mediaTab, setMediaTab] = useState("photos");

  // Admin inputs
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [changeRequestNotes, setChangeRequestNotes] = useState("");

  // Quality flags
  const [qualityFlags, setQualityFlags] = useState({
    missingPhotos: false,
    missingContract: false,
    lowQualityMedia: false,
    incompleteInfo: false,
  });

  // Confirmation checkbox
  const [dealVerified, setDealVerified] = useState(false);

  // Financial editing
  const [editingFinancials, setEditingFinancials] = useState(false);
  const [editRehab, setEditRehab] = useState("");
  const [editARV, setEditARV] = useState("");
  const [editWholesaleFee, setEditWholesaleFee] = useState("");
  const [savingFinancials, setSavingFinancials] = useState(false);

  const { openPresignedUrl } = usePresignedUrl();

  // Hydrated S3 signed URLs for {photos, videos, documents} keyed by category key
  const [mediaUrls, setMediaUrls] = useState({
    photos: {},
    videos: {},
    documents: {},
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (deal && open) {
      fetchDealDetails();
      // Reset state
      setAdminNotes("");
      setRejectionReason("");
      setChangeRequestNotes("");
      setQualityFlags({
        missingPhotos: false,
        missingContract: false,
        lowQualityMedia: false,
        incompleteInfo: false,
      });
      setDealVerified(false);
      setActiveSection("overview");
    }
  }, [deal, open]);

  const fetchDealDetails = async () => {
    if (!deal) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/deals/${deal.deal_id}`, {
        withCredentials: true,
        headers: getAuthHeaders(),
      });
      setDetailData(response.data);
      // Hydrate signed S3 view URLs for all media so we can render previews
      const d = response.data?.deal || response.data;
      const hydrate = async (mediaMap) => {
        const urls = {};
        await Promise.all(
          Object.entries(mediaMap || {}).map(async ([key, meta]) => {
            if (!meta?.storage_path) return;
            try {
              urls[key] = await fetchViewUrl(meta.storage_path);
            } catch (_) {}
          }),
        );
        return urls;
      };
      const [photos, videos, documents] = await Promise.all([
        hydrate(d?.photos),
        hydrate(d?.videos),
        hydrate(d?.documents),
      ]);
      setMediaUrls({ photos, videos, documents });
    } catch (error) {
      console.error("Failed to fetch deal details:", error);
      toast.error("Failed to load deal details");
    } finally {
      setLoading(false);
    }
  };

  const handleModeration = async (action) => {
    if (!deal) return;

    // Validation
    if (action === "reject" && !rejectionReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }

    if (action === "request_changes" && !changeRequestNotes.trim()) {
      toast.error("Please describe what changes are needed");
      return;
    }

    if (action === "approve" && !dealVerified) {
      toast.error("Please verify the deal is complete and trustworthy");
      return;
    }

    // Build notes based on action
    let notes = adminNotes;
    if (action === "reject") {
      notes = `REJECTION REASON: ${rejectionReason}${adminNotes ? `\n\nAdditional Notes: ${adminNotes}` : ""}`;
    } else if (action === "request_changes") {
      notes = `CHANGES REQUESTED: ${changeRequestNotes}${adminNotes ? `\n\nAdditional Notes: ${adminNotes}` : ""}`;
    }

    // Add quality flags if any
    const activeFlags = Object.entries(qualityFlags)
      .filter(([_, v]) => v)
      .map(([k, _]) => k);
    if (activeFlags.length > 0) {
      notes += `\n\nQuality Flags: ${activeFlags.join(", ")}`;
    }

    setActionLoading(action);
    try {
      await axios.post(
        `${API}/admin/deals/${deal.deal_id}/moderate`,
        { action, admin_notes: notes },
        { withCredentials: true, headers: getAuthHeaders() },
      );

      const messages = {
        approve: "Deal approved and published to investors!",
        reject: "Deal has been rejected",
        request_changes: "Change request sent to submitter",
      };

      toast.success(messages[action]);
      onDealUpdated();
    } catch (error) {
      const message = error.response?.data?.detail || "Action failed";
      toast.error(message);
    } finally {
      setActionLoading(null);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "$0";
    return "$" + value.toLocaleString();
  };

  const handleSaveFinancials = async () => {
    if (!deal) return;
    setSavingFinancials(true);
    try {
      const payload = {};
      if (editRehab) payload.estimated_rehab_cost = parseCurrency(editRehab);
      if (editARV) payload.estimated_arv = parseCurrency(editARV);
      if (editWholesaleFee)
        payload.wholesale_fee = parseCurrency(editWholesaleFee);

      await axios.put(
        `${API}/admin/deals/${deal.deal_id}/financials`,
        payload,
        {
          headers: getAuthHeaders(),
        },
      );
      toast.success("Financials updated successfully");
      setEditingFinancials(false);
      await fetchDealDetails();
      if (onDealUpdated) onDealUpdated();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to update financials",
      );
    } finally {
      setSavingFinancials(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPhotoUrl = (key) => {
    if (dealData?.photos?.[key]?.url) {
      return dealData.photos[key].url;
    }
    return null;
  };

  if (!deal) return null;

  const dealData = detailData?.deal || deal;
  const submitter = detailData?.submitter;
  const investor = detailData?.investor;

  const profit =
    dealData.estimated_arv -
    dealData.asking_price -
    dealData.estimated_rehab_cost;
  const roi = ((profit / dealData.asking_price) * 100).toFixed(1);

  const isPending = dealData.status === "pending";
  const hasAnyFlags = Object.values(qualityFlags).some((v) => v);

  // Count uploaded media
  const photoCount = dealData.photos
    ? Object.values(dealData.photos).filter((p) => p?.storage_path).length
    : 0;
  const videoCount = dealData.videos
    ? Object.values(dealData.videos).filter((v) => v?.storage_path).length
    : 0;
  const docCount = dealData.documents
    ? Object.values(dealData.documents).filter((d) => d?.storage_path).length
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[1100px] max-h-[95vh] overflow-hidden p-0 flex flex-col"
        data-testid="deal-review-modal"
      >
        <DialogTitle className="sr-only">Review deal</DialogTitle>
        <DialogDescription className="sr-only">
          Review the submitted deal details, media, documents, quality checks,
          and approval decision.
        </DialogDescription>
        {/* Header */}
        <div className="flex-shrink-0 bg-card border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              {dealData?.nda_signed?.[0]?.storage_path && (
                <button
                  type="button"
                  onClick={() =>
                    openPresignedUrl({
                      key: dealData.nda_signed[0].storage_path,
                    })
                  }
                  className="inline-flex items-center gap-1 text-xs text-white bg-green-500/90 hover:bg-green-600 px-2 py-1 rounded-md"
                >
                  <Shield className="w-3 h-3" />
                  View NDA
                </button>
              )}
              <Badge className={STATUS_COLORS[dealData.status]}>
                {STATUS_LABELS[dealData.status] || dealData.status}
              </Badge>
              <Badge className={DEAL_TYPE_COLORS[dealData.deal_type]}>
                {DEAL_TYPE_LABELS[dealData.deal_type] || dealData.deal_type}
              </Badge>
              <Badge variant="outline">
                {PROPERTY_TYPE_LABELS[dealData.property_type] ||
                  dealData.property_type}
              </Badge>
              {hasAnyFlags && (
                <Badge className="bg-yellow-500 text-white">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Flags Added
                </Badge>
              )}
            </div>
          </div>
          <div className="mt-3">
            <h2 className="text-xl font-heading font-bold text-foreground">
              {dealData.property_address}
            </h2>
            <p className="text-muted-foreground flex items-center gap-1 mt-1">
              <MapPin className="w-4 h-4" />
              {dealData.city}, {dealData.state} {dealData.zip_code}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
              <p className="mt-2 text-muted-foreground">
                Loading deal details...
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              {/* ========== SECTION A: SUBMISSION OVERVIEW ========== */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Section A: Submission Overview
                </h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Submitter Info */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <UserCircle className="w-4 h-4" />
                        Submitter Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {submitter ? (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground">
                              Name
                            </p>
                            <p className="font-medium">{submitter.name}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">
                              Email
                            </p>
                            <p className="font-medium">{submitter.email}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">
                              Role
                            </p>
                            <p className="font-medium capitalize">
                              {submitter.role}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">
                              State
                            </p>
                            <p className="font-medium">
                              {submitter.state || "N/A"}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Loading...
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Property Details */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Home className="w-4 h-4" />
                        Property Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Property Type
                          </p>
                          <p className="font-medium">
                            {PROPERTY_TYPE_LABELS[dealData.property_type]}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Deal Type
                          </p>
                          <p className="font-medium">
                            {DEAL_TYPE_LABELS[dealData.deal_type]}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Bedrooms
                          </p>
                          <p className="font-medium">
                            {dealData.bedrooms ?? "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Bathrooms
                          </p>
                          <p className="font-medium">
                            {dealData.bathrooms ?? "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Sq. Footage
                          </p>
                          <p className="font-medium">
                            {dealData.square_footage
                              ? dealData.square_footage.toLocaleString()
                              : "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Lot Size
                          </p>
                          <p className="font-medium">
                            {dealData.lot_size || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Date Submitted
                          </p>
                          <p className="font-medium">
                            {formatDate(dealData.created_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">
                            Deal ID
                          </p>
                          <p className="font-medium text-xs">
                            {dealData.deal_id}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Financials */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Financial Details
                      </CardTitle>
                      {!editingFinancials ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditRehab(
                              formatCurrencyInput(
                                dealData.estimated_rehab_cost || "",
                              ),
                            );
                            setEditARV(
                              formatCurrencyInput(dealData.estimated_arv || ""),
                            );
                            setEditWholesaleFee(
                              formatCurrencyInput(dealData.wholesale_fee || ""),
                            );
                            setEditingFinancials(true);
                          }}
                          data-testid="edit-financials-btn"
                        >
                          <Edit3 className="w-4 h-4 mr-1" /> Edit
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingFinancials(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSaveFinancials}
                            disabled={savingFinancials}
                            className="bg-blue-500 hover:bg-blue-600"
                            data-testid="save-financials-btn"
                          >
                            {savingFinancials ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : null}
                            Save
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editingFinancials ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs">Rehab Cost ($)</Label>
                          <input
                            inputMode="decimal"
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={editRehab}
                            onChange={(e) =>
                              setEditRehab(formatCurrencyInput(e.target.value))
                            }
                            data-testid="edit-rehab-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">ARV ($)</Label>
                          <input
                            inputMode="decimal"
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={editARV}
                            onChange={(e) =>
                              setEditARV(formatCurrencyInput(e.target.value))
                            }
                            data-testid="edit-arv-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Wholesale Fee ($)</Label>
                          <input
                            inputMode="decimal"
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={editWholesaleFee}
                            onChange={(e) =>
                              setEditWholesaleFee(
                                formatCurrencyInput(e.target.value),
                              )
                            }
                            data-testid="edit-wholesale-fee-input"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground mb-1">
                            Purchase Price
                          </p>
                          <p className="text-lg font-bold">
                            {formatCurrency(dealData.asking_price)}
                          </p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground mb-1">
                            Rehab Cost
                          </p>
                          <p className="text-lg font-bold">
                            {formatCurrency(dealData.estimated_rehab_cost)}
                          </p>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground mb-1">
                            ARV
                          </p>
                          <p className="text-lg font-bold">
                            {formatCurrency(dealData.estimated_arv)}
                          </p>
                        </div>
                        <div className="bg-green-500/10 rounded-lg p-3 text-center">
                          <p className="text-xs text-green-600 dark:text-green-400 mb-1">
                            Est. Profit
                          </p>
                          <p className="text-lg font-bold text-green-600 dark:text-green-400">
                            {formatCurrency(profit)}
                          </p>
                        </div>
                        <div className="bg-purple-500/10 rounded-lg p-3 text-center">
                          <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">
                            Wholesale Fee
                          </p>
                          <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                            {dealData.wholesale_fee
                              ? formatCurrency(dealData.wholesale_fee)
                              : "N/A"}
                          </p>
                        </div>
                        <div className="bg-blue-500/10 rounded-lg p-3 text-center">
                          <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                            ROI
                          </p>
                          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                            {roi}%
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Notes */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {dealData.repair_notes && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Wrench className="w-4 h-4" />
                          Scope of Work
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm whitespace-pre-wrap">
                          {dealData.repair_notes}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {dealData.additional_notes && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Additional Notes
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm whitespace-pre-wrap">
                          {dealData.additional_notes}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              <Separator />

              {/* ========== SECTION B: MEDIA VERIFICATION ========== */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                  <Camera className="w-5 h-5 text-green-500" />
                  Section B: Media Verification
                </h3>

                <Tabs value={mediaTab} onValueChange={setMediaTab}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger
                      value="photos"
                      className="flex items-center gap-2"
                    >
                      <Camera className="w-4 h-4" />
                      Photos ({photoCount})
                    </TabsTrigger>
                    <TabsTrigger
                      value="videos"
                      className="flex items-center gap-2"
                    >
                      <Video className="w-4 h-4" />
                      Videos ({videoCount})
                    </TabsTrigger>
                    <TabsTrigger
                      value="documents"
                      className="flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Documents ({docCount})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="photos" className="mt-4">
                    {photoCount > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(dealData.photos || {}).map(
                          ([key, photo]) => {
                            if (!photo?.storage_path) return null;
                            const viewUrl = mediaUrls.photos[key];
                            return (
                              <a
                                key={key}
                                href={viewUrl || "#"}
                                target={viewUrl ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  if (!viewUrl) e.preventDefault();
                                }}
                                className="relative aspect-video rounded-lg overflow-hidden bg-muted group block"
                                data-testid={`admin-photo-${key}`}
                              >
                                {viewUrl ? (
                                  <img
                                    src={viewUrl}
                                    alt={key}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Camera className="w-6 h-6 text-muted-foreground" />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Button size="sm" variant="secondary">
                                    <Eye className="w-4 h-4 mr-1" />
                                    View
                                  </Button>
                                </div>
                                <p className="absolute bottom-1 left-1 text-xs bg-black/70 text-white px-2 py-0.5 rounded">
                                  {key.replace(/_/g, " ")}
                                </p>
                              </a>
                            );
                          },
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <ImageOff className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No photos uploaded</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="videos" className="mt-4">
                    {videoCount > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.entries(dealData.videos || {}).map(
                          ([key, video]) => {
                            if (!video?.storage_path) return null;
                            const viewUrl = mediaUrls.videos[key];
                            return (
                              <a
                                key={key}
                                href={viewUrl || "#"}
                                target={viewUrl ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                  if (!viewUrl) e.preventDefault();
                                }}
                                className="relative aspect-video rounded-lg overflow-hidden bg-muted group block"
                                data-testid={`admin-video-${key}`}
                              >
                                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                  <Play className="w-12 h-12 text-white opacity-70" />
                                </div>
                                <p className="absolute bottom-1 left-1 text-xs bg-black/70 text-white px-2 py-0.5 rounded">
                                  {key.replace(/_/g, " ")}
                                </p>
                              </a>
                            );
                          },
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No videos uploaded</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="documents" className="mt-4">
                    {docCount > 0 ? (
                      <div className="space-y-2">
                        {Object.entries(dealData.documents || {}).map(
                          ([key, doc]) => {
                            if (!doc?.storage_path) return null;
                            const viewUrl = mediaUrls.documents[key];
                            return (
                              <div
                                key={key}
                                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <FileText className="w-5 h-5 text-blue-500 shrink-0" />
                                  <div className="min-w-0">
                                    <p className="font-medium capitalize truncate">
                                      {key.replace(/_/g, " ")}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                      {doc.filename || "Document"}
                                    </p>
                                  </div>
                                </div>
                                {viewUrl ? (
                                  <a
                                    href={viewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`admin-doc-${key}`}
                                  >
                                    <Button size="sm" variant="outline">
                                      <Download className="w-4 h-4 mr-1" />
                                      View / Download
                                    </Button>
                                  </a>
                                ) : (
                                  <Button size="sm" variant="outline" disabled>
                                    <Download className="w-4 h-4 mr-1" />
                                    Loading…
                                  </Button>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileWarning className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No documents uploaded</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                {/* Quick Flags */}
                {isPending && (
                  <Card className="border-yellow-500/50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                        <AlertTriangle className="w-4 h-4" />
                        Quick Quality Flags
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="flag-photos"
                            checked={qualityFlags.missingPhotos}
                            onCheckedChange={(checked) =>
                              setQualityFlags((prev) => ({
                                ...prev,
                                missingPhotos: checked,
                              }))
                            }
                          />
                          <Label
                            htmlFor="flag-photos"
                            className="text-sm cursor-pointer"
                          >
                            Missing Photos
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="flag-contract"
                            checked={qualityFlags.missingContract}
                            onCheckedChange={(checked) =>
                              setQualityFlags((prev) => ({
                                ...prev,
                                missingContract: checked,
                              }))
                            }
                          />
                          <Label
                            htmlFor="flag-contract"
                            className="text-sm cursor-pointer"
                          >
                            Missing Contract
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="flag-quality"
                            checked={qualityFlags.lowQualityMedia}
                            onCheckedChange={(checked) =>
                              setQualityFlags((prev) => ({
                                ...prev,
                                lowQualityMedia: checked,
                              }))
                            }
                          />
                          <Label
                            htmlFor="flag-quality"
                            className="text-sm cursor-pointer"
                          >
                            Low Quality Media
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="flag-incomplete"
                            checked={qualityFlags.incompleteInfo}
                            onCheckedChange={(checked) =>
                              setQualityFlags((prev) => ({
                                ...prev,
                                incompleteInfo: checked,
                              }))
                            }
                          />
                          <Label
                            htmlFor="flag-incomplete"
                            className="text-sm cursor-pointer"
                          >
                            Incomplete Info
                          </Label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              <Separator />

              {/* ========== SECTION C: FINANCIAL VALIDATION (READ-ONLY FOR MVP) ========== */}
              <div className="space-y-4">
                <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-500" />
                  Section C: Financial Validation
                  <Badge variant="outline" className="text-xs">
                    Read-only
                  </Badge>
                </h3>

                <Card className="bg-muted/30">
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          Rehab Estimate
                        </Label>
                        <p className="font-medium mt-1">
                          {formatCurrency(dealData.estimated_rehab_cost)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          ARV
                        </Label>
                        <p className="font-medium mt-1">
                          {formatCurrency(dealData.estimated_arv)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          Profit Calculation
                        </Label>
                        <p className="font-medium mt-1 text-green-600">
                          {formatCurrency(profit)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          Wholesale Fee
                        </Label>
                        <p className="font-medium mt-1">
                          {dealData.wholesale_fee
                            ? formatCurrency(dealData.wholesale_fee)
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* ========== ADMIN DECISION ACTIONS ========== */}
              {isPending && (
                <>
                  <Separator />

                  <div className="space-y-4">
                    <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                      <Shield className="w-5 h-5 text-red-500" />
                      Admin Decision
                    </h3>

                    {/* Verification Checkbox */}
                    <Card className="border-blue-500/50">
                      <CardContent className="pt-4">
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="deal-verified"
                            checked={dealVerified}
                            onCheckedChange={setDealVerified}
                            className="mt-1"
                          />
                          <div>
                            <Label
                              htmlFor="deal-verified"
                              className="cursor-pointer font-medium"
                            >
                              I verify this deal is complete and trustworthy
                            </Label>
                            <p className="text-sm text-muted-foreground mt-1">
                              By checking this, you confirm you have reviewed
                              all submission details, media, and financials.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Admin Notes */}
                    <div>
                      <Label htmlFor="admin-notes" className="text-sm">
                        Internal Notes (optional)
                      </Label>
                      <Textarea
                        id="admin-notes"
                        placeholder="Add any internal notes about this deal..."
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        className="mt-2"
                        rows={2}
                        data-testid="admin-notes-input"
                      />
                    </div>

                    {/* Action Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* APPROVE */}
                      <Card
                        className={`border-2 transition-colors ${dealVerified ? "border-green-500 bg-green-500/5" : "border-muted opacity-60"}`}
                      >
                        <CardContent className="pt-4 space-y-3">
                          <div className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-semibold">
                              1. Approve & Publish
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Deal goes live and becomes visible to all investors.
                            Notification sent.
                          </p>
                          <Button
                            className="w-full bg-green-500 hover:bg-green-600"
                            onClick={() => handleModeration("approve")}
                            disabled={actionLoading !== null || !dealVerified}
                            data-testid="approve-deal-btn"
                          >
                            {actionLoading === "approve" ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Sparkles className="w-4 h-4 mr-2" />
                            )}
                            Approve & Go Live
                          </Button>
                        </CardContent>
                      </Card>

                      {/* REQUEST CHANGES */}
                      <Card className="border-2 border-yellow-500/50 hover:border-yellow-500 transition-colors">
                        <CardContent className="pt-4 space-y-3">
                          <div className="flex items-center gap-2 text-yellow-600">
                            <Edit3 className="w-5 h-5" />
                            <span className="font-semibold">
                              2. Request Changes
                            </span>
                          </div>
                          <Textarea
                            placeholder="Describe what changes are needed..."
                            value={changeRequestNotes}
                            onChange={(e) =>
                              setChangeRequestNotes(e.target.value)
                            }
                            className="text-sm"
                            rows={2}
                            data-testid="change-request-notes"
                          />
                          <Button
                            variant="outline"
                            className="w-full border-yellow-500 text-yellow-600 hover:bg-yellow-500/10"
                            onClick={() => handleModeration("request_changes")}
                            disabled={actionLoading !== null}
                            data-testid="request-changes-btn"
                          >
                            {actionLoading === "request_changes" ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            Send to Submitter
                          </Button>
                        </CardContent>
                      </Card>

                      {/* REJECT */}
                      <Card className="border-2 border-red-500/50 hover:border-red-500 transition-colors">
                        <CardContent className="pt-4 space-y-3">
                          <div className="flex items-center gap-2 text-red-500">
                            <XCircle className="w-5 h-5" />
                            <span className="font-semibold">
                              3. Reject Deal
                            </span>
                          </div>
                          <Textarea
                            placeholder="Rejection reason (required)..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            className="text-sm border-red-200 focus:border-red-500"
                            rows={2}
                            data-testid="rejection-reason"
                          />
                          <Button
                            variant="outline"
                            className="w-full border-red-500 text-red-500 hover:bg-red-500/10"
                            onClick={() => handleModeration("reject")}
                            disabled={actionLoading !== null}
                            data-testid="reject-deal-btn"
                          >
                            {actionLoading === "reject" ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Ban className="w-4 h-4 mr-2" />
                            )}
                            Reject Deal
                          </Button>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </>
              )}

              {/* For non-pending deals, show history */}
              {!isPending && (
                <>
                  <Separator />
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Deal History
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                          <div>
                            <p className="font-medium">Submitted</p>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(dealData.created_at)} by{" "}
                              {dealData.submitted_by_name}
                            </p>
                          </div>
                        </div>

                        {dealData.reviewed_at && (
                          <div className="flex items-start gap-3">
                            <div
                              className={`w-2 h-2 rounded-full mt-2 ${dealData.status === "rejected" ? "bg-red-500" : "bg-blue-500"}`}
                            />
                            <div>
                              <p className="font-medium">
                                {STATUS_LABELS[dealData.status]}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(dealData.reviewed_at)} by{" "}
                                {dealData.reviewed_by_name}
                              </p>
                              {dealData.admin_notes && (
                                <p className="text-sm mt-1 p-2 bg-muted rounded">
                                  {dealData.admin_notes}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {dealData.reserved_at && (
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 rounded-full bg-purple-500 mt-2" />
                            <div>
                              <p className="font-medium">Reserved</p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(dealData.reserved_at)} by{" "}
                                {dealData.reserved_by_name}
                              </p>
                            </div>
                          </div>
                        )}

                        {dealData.nda_signed?.signed && (
                          <div className="flex items-start gap-3">
                            <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                            <div className="space-y-1">
                              <p className="font-medium">Deal NDA Signed</p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(dealData.nda_signed.signed)} —{" "}
                                {dealData.nda_signed.signed_name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Assigned by: {dealData.nda_signed.assigned_by}
                              </p>
                              {dealData.nda_signed.nda_updated_at && (
                                <p className="text-xs text-muted-foreground">
                                  NDA replaced:{" "}
                                  {formatDate(
                                    dealData.nda_signed.nda_updated_at,
                                  )}{" "}
                                  by {dealData.nda_signed.nda_updated_by}
                                </p>
                              )}
                              {dealData.nda_signed.s3_key && (
                                <NdaViewButton
                                  s3Key={dealData.nda_signed.s3_key}
                                />
                              )}
                              {/* hide ReplaceNdaToggle if nda_signed */}
                              {!dealData.nda_signed?.s3_key && (
                                <ReplaceNdaToggle
                                  dealId={dealData.deal_id}
                                  onReplaced={fetchDealDetails}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        )}

        {/* Footer for non-pending deals */}
        {!isPending && !loading && (
          <div className="flex-shrink-0 bg-card border-t p-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onOpenChange(false)}
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DealReviewModal;
